import React, { useContext, useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components/macro";
import { useTranslation } from "react-i18next";
import type { TFunction } from "react-i18next";
import {
  ButtonContainer,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
  SecondaryButtonLargeV2,
} from "../Buttons/Buttons";
import { TextField } from "../TextFields/TextFields";
import type { LeadOtpSettings } from "../../types/types";
import type { LeadFormOtpStep } from "./useLeadFormOtp";
import { Form } from "../../layout/FormLayout";
import { AuthLogo } from "../../layout/publicPageLayout";
import { isAxiosError, useFormWrapper } from "../../util/util";
import { Notifications } from "../Notifications/NotificationsContext";
import { strings } from "../../util/strings";
import { Text2xlRegular, TextSmRegular } from "../Typography/TypographyV2";

const PageWrapper = styled.div`
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_neutral_gray200")};
  padding: ${({ theme }) => theme.resolve("spacing_xxs")} 0
    ${({ theme }) => theme.resolve("spacing_xl")}
    ${({ theme }) => theme.resolve("spacing_2xl")};
`;

const FormCenter = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const FormCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 452px;

  .form {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.resolve("spacing_md")};
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};

  img {
    margin: 0;
  }
`;

type LeadFormOtpVerificationProps = {
  otpStep: LeadFormOtpStep;
  settings: LeadOtpSettings;
  loading: boolean;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
};

type FormInputs = {
  email: string;
  otp_code: string;
};

function getSecondsUntil(targetIso: string): number {
  const target = new Date(targetIso).getTime();
  return Math.max(0, Math.ceil((target - Date.now()) / 1000));
}

function formatResendCooldownDuration(
  t: TFunction,
  totalSeconds: number
): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];

  if (minutes > 0) {
    parts.push(
      minutes === 1
        ? t("{{count}} minute", { count: minutes })
        : t("{{count}} minutes", { count: minutes })
    );
  }

  if (seconds > 0 || minutes === 0) {
    parts.push(
      seconds === 1
        ? t("{{count}} second", { count: seconds })
        : t("{{count}} seconds", { count: seconds })
    );
  }

  return parts.join(" ");
}

function getResendCooldownMessage(
  t: TFunction,
  resendCooldownSeconds: number
): string {
  return t(
    "Code sent via email. Please wait {{duration}} to generate a new code.",
    { duration: formatResendCooldownDuration(t, resendCooldownSeconds) }
  );
}

function getVerifyErrorMessage(error: unknown, t: TFunction): string {
  if (isAxiosError(error) && error.response?.data?.message) {
    const data = error.response.data;
    const message = String(data.message);
    if (message === "code_expired") {
      return t("Code is expired. Please generate new code.");
    }
    if (message === "invalid_code") {
      const remainingAttempts = data.remaining_attempts;
      if (typeof remainingAttempts === "number") {
        return t(
          "Invalid code. Please try again or generate a new code. {{count}} attempts remaining.",
          { count: remainingAttempts }
        );
      }
      return t("Invalid code. Please try again or generate a new code.");
    }
    if (message === "session_expired") {
      return t("Verification session expired. Please restart form submission.");
    }
    if (message === "max_attempts_reached") {
      return t("Max attempts reached. Please try again in 1 hour.");
    }
    return message;
  }
  return t("Invalid code. Please try again or generate a new code.");
}

function getResendErrorMessage(
  error: unknown,
  t: TFunction,
  resendCooldownSeconds: number
): string {
  if (isAxiosError(error) && error.response?.data?.message) {
    const message = String(error.response.data.message);
    if (message === "max_attempts_reached") {
      return t("Max attempts reached. Please try again in 1 hour.");
    }
    if (message === "resend_cooldown") {
      return getResendCooldownMessage(t, resendCooldownSeconds);
    }
    if (message === "session_expired") {
      return t("Verification session expired. Please restart form submission.");
    }
    return message;
  }
  return t("Unable to generate a new code. Please try again.");
}

function normalizeOtpCode(raw: string, maxLength: number): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, maxLength);
}

export const LeadFormOtpVerification: React.FC<LeadFormOtpVerificationProps> =
  ({
    otpStep,
    settings,
    loading,
    onVerify,
    onResend,
    onCancel,
    submitLabel,
  }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { notifySuccess, notifyError } = useContext(Notifications);
    const isSubmittingRef = useRef(false);
    const [resendSeconds, setResendSeconds] = useState(() =>
      getSecondsUntil(otpStep.resendAvailableAt)
    );
    const resendCooldownSeconds = settings.resend_cooldown_seconds;
    const codeLength = settings.code_length;
    const logoUrl = theme.publicLogoUrl || theme.portalLogoUrl;
    const verifySubmitLabel = submitLabel ?? t("Submit");
    const resendCooldownMessage = getResendCooldownMessage(
      t,
      resendCooldownSeconds
    );

    const { handleSubmit, register, formState, errors, setValue } =
      useFormWrapper<FormInputs>({
        defaultValues: {
          email: otpStep.email,
          otp_code: "",
        },
      });

    useEffect(() => {
      setResendSeconds(getSecondsUntil(otpStep.resendAvailableAt));
    }, [otpStep.resendAvailableAt]);

    useEffect(() => {
      if (resendSeconds <= 0) {
        return;
      }
      const timer = window.setInterval(() => {
        setResendSeconds(getSecondsUntil(otpStep.resendAvailableAt));
      }, 1000);
      return () => window.clearInterval(timer);
    }, [otpStep.resendAvailableAt, resendSeconds]);

    const onSubmit = async ({ otp_code }: FormInputs) => {
      if (isSubmittingRef.current || loading) {
        return;
      }
      isSubmittingRef.current = true;
      try {
        await onVerify(otp_code.trim());
      } catch (submitError) {
        notifyError(getVerifyErrorMessage(submitError, t), {
          error: submitError,
        });
        if (isAxiosError(submitError) && submitError.response?.status === 410) {
          onCancel();
        }
      } finally {
        isSubmittingRef.current = false;
      }
    };

    const handleResend = async () => {
      if (resendSeconds > 0) {
        notifyError(resendCooldownMessage);
        return;
      }
      try {
        await onResend();
        notifySuccess(resendCooldownMessage);
      } catch (resendError) {
        notifyError(
          getResendErrorMessage(resendError, t, resendCooldownSeconds),
          { error: resendError }
        );
        if (isAxiosError(resendError) && resendError.response?.status === 410) {
          onCancel();
        }
      }
    };

    return (
      <PageWrapper>
        <PageHeader>
          <Text2xlRegular>{t("Verify Email")}</Text2xlRegular>
          <TextSmRegular>
            {t("Please verify your email to continue")}
          </TextSmRegular>
        </PageHeader>

        <FormCenter>
          <FormCard>
            {logoUrl && (
              <LogoWrapper>
                <AuthLogo src={logoUrl} alt={t("Storefront logo")} />
              </LogoWrapper>
            )}

            <Form noValidate onSubmit={handleSubmit(onSubmit)} className="form">
              <TextField
                name="email"
                label={t("Email Address")}
                theref={register()}
                formState={formState}
                errors={errors}
                type="email"
                defaultValue={otpStep.email}
                readOnly
              />

              <TextField
                name="otp_code"
                label={t("OTP Code")}
                theref={register({
                  required: strings(t).thisIsARequiredField,
                  maxLength: codeLength,
                })}
                formState={formState}
                errors={errors}
                type="text"
                autoComplete="one-time-code"
                onChange={(event) => {
                  const normalized = normalizeOtpCode(
                    event.target.value,
                    codeLength
                  );
                  event.target.value = normalized;
                  setValue("otp_code", normalized, { shouldValidate: true });
                }}
              />

              <SecondaryButtonLargeV2
                type="button"
                disabled={loading}
                onClick={handleResend}
                fullWidth={false}
                style={{ width: "fit-content" }}
              >
                {t("Generate New Code")}
              </SecondaryButtonLargeV2>

              <ButtonContainer>
                <TertiaryButtonLargeV2 disabled={loading} onClick={onCancel}>
                  {t("Cancel")}
                </TertiaryButtonLargeV2>
                <PrimaryButtonLargeV2
                  type="submit"
                  loading={loading}
                  disabled={loading}
                >
                  {verifySubmitLabel}
                </PrimaryButtonLargeV2>
              </ButtonContainer>
            </Form>
          </FormCard>
        </FormCenter>
      </PageWrapper>
    );
  };
